import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import { modeFromCode } from '@/lib/icons'
import { queryKeys } from '@/lib/query'
import type { CargoDraft } from '@/features/quotes/components/CargoFormSheet'
import type { CostLineDraft } from '@/features/quotes/components/CostLineFormSheet'
import type { QuoteDraft } from '@/features/quotes/quoteDraft'
import { MOCK_QUOTES, USE_MOCKS } from '@/mocks/fixtures'
import {
  MOCK_QUOTE_DETAIL,
  MOCK_QUOTE_HISTORY,
  MOCK_QUOTE_STATISTICS,
} from '@/mocks/quotes'
import type {
  CargoPayload,
  CargoPosition,
  ConversionResult,
  CostLine,
  CostLinePayload,
  OfferPreview,
  ParticipantPayload,
  PipelineStatistics,
  QuoteActionEntry,
  QuoteDetail,
  QuoteParticipant,
  QuotePayload,
  QuoteRevision,
  QuoteStatus,
  QuoteSummary,
  QuoteTeamMember,
} from '@/types/domain'

/* The quote wire spells the transport mode as a directory CODE
 * (`transportModeCode`, "RAIL" — dto.go QuoteDTO); the domain and every card
 * speak lowercase modes. Composed here at the fetch, ONCE, for the reason
 * caseWire.ts exists: a screen reaching for the code directly would be a
 * second place the spelling lives. An unset or unrecognised code stays
 * undefined, and the card drops its mode rather than guessing one.
 *
 * The SELECTED route option's code wins over the header's (Story 2.4): the
 * lane the card states is the option's, and its mode must be the same
 * routing's — a card saying "Guangzhou → Ashgabat · sea" off a rail option
 * because the client ASKED about sea would be lying about the offer. */
function withMode<T extends QuoteSummary | QuoteDetail>(
  wire: T & { transportModeCode?: string },
): T {
  return {
    ...wire,
    mode:
      wire.mode ??
      modeFromCode(wire.routeModeCode) ??
      modeFromCode(wire.transportModeCode),
  }
}

/** `enabled` for callers that only need the list conditionally — the case
 *  sheet's quote picker, which exists on one of its two variants. */
export function useQuotes({ enabled }: { enabled?: boolean } = {}) {
  return useQuery<QuoteSummary[]>({
    queryKey: queryKeys.quotes.list(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_QUOTES
        : (await api.get<{ items: QuoteSummary[] }>('/quotes')).items.map(withMode),
  })
}

/** The pipeline's win/loss figures over a date range (FR-15, Story 2.7).
 *
 * Its own read rather than arithmetic over `useQuotes` above, and that is the
 * decision worth stating: Expired is COMPUTED from the validity date at read
 * time and never stored (AD-24), so a client counting the list's rows would
 * need a second expression of a rule the server already owns — and the two
 * would disagree the first time either moved. The server counts, this fetches.
 *
 * Both ends of the range are OMITTED when absent rather than sent empty. The
 * endpoint reads a blank parameter as unbounded and would answer the same, but
 * "all time" asking for nothing is the honest request, and it keeps the URL the
 * cache key describes identical to the one that goes out.
 *
 * No `enabled`: the widget is mounted with the pipeline and its figures are
 * what the surface is read for. Nothing here retries a scope refusal either —
 * the shared client already declines to retry a decision the server has made.
 */
export function useQuoteStatistics(range: { from?: string; to?: string }) {
  return useQuery<PipelineStatistics>({
    queryKey: queryKeys.quotes.statistics(range),
    queryFn: () => {
      if (USE_MOCKS) return Promise.resolve(MOCK_QUOTE_STATISTICS)

      const params = new URLSearchParams()
      if (range.from) params.set('from', range.from)
      if (range.to) params.set('to', range.to)
      const query = params.toString()

      return api.get<PipelineStatistics>(
        `/quotes/statistics${query === '' ? '' : `?${query}`}`,
      )
    },
  })
}

export function useQuote(quoteId: string) {
  return useQuery<QuoteDetail>({
    queryKey: queryKeys.quotes.detail(quoteId),
    queryFn: () => {
      if (!USE_MOCKS)
        return api.get<QuoteDetail>(`/quotes/${quoteId}`).then(withMode)

      const record = MOCK_QUOTE_DETAIL[quoteId]
      if (!record) {
        return Promise.reject(
          new ApiError({
            type: 'https://asagateway.com/problems/not-found',
            title: 'Not found',
            status: 404,
          }),
        )
      }
      return Promise.resolve(record)
    },
  })
}

/* The quote's action history (the History tab): who did what to this quote,
 * newest first, read from the global action log the server keeps. Enabled only
 * while its tab is open — the log is append-heavy and most visits never open
 * it, so fetching it with the detail would tax every read for a section few
 * look at. */
export function useQuoteHistory(quoteId: string, { enabled }: { enabled?: boolean } = {}) {
  return useQuery<QuoteActionEntry[]>({
    queryKey: queryKeys.quotes.history(quoteId),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? (MOCK_QUOTE_HISTORY[quoteId] ?? [])
        : (await api.get<{ items: QuoteActionEntry[] }>(`/quotes/${quoteId}/history`)).items,
  })
}

/* Mutations.
 *
 * Not optimistic, and deliberately so at the header. UX-DR-24's confirmed-write
 * rule does not reach here — a quote header is not a finance table — but a
 * created quote is NUMBERED by the server (FR-5), and a number is not something
 * the client can guess and then correct. The sheet stays open with `pending`
 * until the server answers with the real record.
 *
 * The optimistic surface this epic owns is cost-line entry (Story 2.3), where
 * pressing Enter must render the next row without waiting.
 */

function useInvalidateQuotes() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all })
  }
}

/** The sheet's draft as the server takes it (FR-23: numbers, not text).
 *
 * Here rather than in each page that submits one, so the quote list and the
 * dashboard cannot disagree about what a create sends. (Beside the mutation
 * rather than beside the sheet because a component file may export only
 * components.) */
export function toQuotePayload(draft: QuoteDraft): QuotePayload {
  const count = (raw: string): number | undefined =>
    raw.trim() === '' ? undefined : Number(raw)

  /* The header measures stay strings (AD-9), like the cargo ones. Sent as ''
     rather than omitted when blank: an empty override is the user CLEARING a
     correction they no longer stand behind, and an omitted key would leave the
     old figure in place — the opposite of what emptying the field means. */
  const measure = (raw: string): string => raw.trim().replace(',', '.')

  /* A blank optional field is OMITTED rather than sent as ''.
   *
   * The server reads an absent key and an empty string identically on these —
   * both land as Go's zero value — so this is about what the wire SAYS rather
   * than about behaviour: `undefined` states "this quote has none", which is what
   * the DTO's own omitempty means when it comes back. The measures are the
   * deliberate exception below, because there an empty string is an instruction.
   */
  const text = (raw: string): string | undefined => raw.trim() || undefined

  return {
    clientId: draft.clientId,
    clientContactId: text(draft.clientContactId),
    quoteDate: draft.quoteDate,
    validUntil: draft.validUntil || undefined,
    cargoOwner: text(draft.cargoOwner),
    cargoDescription: text(draft.cargoDescription),
    pickupLocation: text(draft.pickupLocation),
    incoterm: draft.incoterm || undefined,
    paymentTerms: draft.paymentTerms || undefined,
    demurrageTerms: text(draft.demurrageTerms),
    deliveryTerms: text(draft.deliveryTerms),
    carrierDisplay: text(draft.carrierDisplay),
    transitDaysMin: count(draft.transitDaysMin),
    transitDaysMax: count(draft.transitDaysMax),
    grossWeightKgOverride: measure(draft.grossWeightKgOverride),
    volumeCbmOverride: measure(draft.volumeCbmOverride),
    chargeableWeightKg: measure(draft.chargeableWeightKg),
    finalPackingListReceived: draft.finalPackingListReceived,
    /* No `participants` key, and its absence is the point rather than an
       omission. A quote does not name its external parties on the way in: they
       are named on the case, and on the detail page's own section, which posts
       them one at a time at /quotes/:id/participants. The server still ACCEPTS
       the key on create — QuotePayload still declares it — so sending [] here
       would be worse than not sending it: the UPDATE shares this builder, and an
       empty array there reads as "remove them all" rather than as silence.

       The internal team (0028) is omitted when empty for that same reason, and
       stays in the create because unlike a party it cannot wait — membership is
       what grants visibility, so it belongs in the create's own transaction.

       Ids straight through — there is nothing to trim or normalise, because
       these were picked from a list rather than typed. */
    team: draft.team.length > 0 ? draft.team : undefined,
  }
}

export function useCreateQuote() {
  const invalidate = useInvalidateQuotes()
  return useMutation<QuoteSummary, Error, QuotePayload>({
    mutationFn: (payload) => api.post<QuoteSummary>('/quotes', payload),
    onSuccess: invalidate,
  })
}

/** The client's word about an issued offer, as the wire speaks it: their
 *  refusal, or "pending" — they have not actually decided — which returns the
 *  quote to Sent and is what the board's undo posts.
 *
 *  THEIR YES IS NOT HERE. Accepting an offer creates the case (FR-19), so it
 *  goes through `useConvertQuote` below; the server refuses "approved" on this
 *  endpoint, and the status it used to write is gone (migration 0050). */
export type ClientAnswer = 'rejected' | 'pending'

/** Records the client's answer (FR-6) — the act behind dragging a card
 *  between Sent and Rejected.
 *
 * OPTIMISTIC on the list cache, deliberately: the board must complete the
 * move on drop (EXPERIENCE.md § Board card), and this is bookkeeping about
 * the client's word, not a money table — the confirmed-write rule (UX-DR-24)
 * begins at case expense lines. A refusal rolls the card back and the
 * refetch restores whatever the server holds. */
export function useClientAnswer() {
  const queryClient = useQueryClient()
  return useMutation<QuoteSummary, Error, { id: string; answer: ClientAnswer }>({
    mutationFn: ({ id, answer }) =>
      api.post<QuoteSummary>(`/quotes/${id}/client-answer`, { answer }),
    onMutate: async ({ id, answer }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.quotes.list() })
      const moved: QuoteStatus = answer === 'pending' ? 'sent' : 'rejected'
      queryClient.setQueryData<QuoteSummary[]>(queryKeys.quotes.list(), (rows) =>
        rows?.map((row) => (row.id === id ? { ...row, status: moved } : row)),
      )
    },
    // Success and failure both end at the server's answer: invalidate rather
    // than trust the optimistic rows, so the board always settles on what is
    // actually stored — including the roll-back after a refusal.
    //
    // The win/loss figures go with it. Rejecting a card is exactly one of the
    // acts those counts are OF, and the widget sits directly above the lane
    // the card was dropped into: a pipeline that moved while the conversion
    // rate over it did not is the list-vs-figures disagreement this module
    // refuses to let happen, and it would last until the tab was refocused.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.statisticsAll })
    },
  })
}

/** Turns an accepted quote into its case (Story 3.1, FR-19, AD-14).
 *
 * This is also HOW the client's acceptance is recorded — there is no status
 * write that says it and no `approved` state to write (see ClientAnswer). The
 * detail page's "Client approved" button calls exactly this.
 *
 * NOT optimistic, and this is the sharpest instance of that rule in the app.
 * Conversion writes a case, its rate and its planned expense lines in one
 * server transaction, and the response carries the case NUMBER — minted by the
 * server, unguessable here. Showing a case before the server has made one, or
 * inventing a number to show beside it, would be a screen claiming a shipment
 * exists when the transaction may still roll back.
 *
 * Both caches are invalidated: the quote has moved to Converted, and a case now
 * exists that the case list has never seen. */
export function useConvertQuote() {
  const queryClient = useQueryClient()
  return useMutation<ConversionResult, Error, string>({
    // No body. Everything conversion needs is already on the quote — a request
    // carrying a rate or a route would be offering the server values it is
    // about to read for itself, and the two disagreeing is precisely the
    // re-typing FR-19 forbids.
    mutationFn: (quoteId) =>
      api.post<ConversionResult>(`/quotes/${quoteId}/conversion`, {}),
    onSuccess: (_result, quoteId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.quotes.detail(quoteId),
      })
      // Converted counts as WON, so the figures above the pipeline are a
      // different set of numbers the moment this returns.
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.statisticsAll })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
    },
  })
}

/* The UPDATE payload, which is not the create payload with an id beside it.
 *
 * PUT /quotes/:id takes the WHOLE record — quoteBody.change() in the Go handler
 * builds a Change from the body's fields directly, so a key the client omits
 * arrives as a zero value and CLEARS the column. Its own comment says so of the
 * measures: "an empty string parses to nil, which CLEARS the override".
 *
 * toQuotePayload was written for the CREATE, and QuoteDraft carries about half
 * of what the wire accepts: no cargoOwner, no cargoDescription, no
 * pickupLocation, no carrierDisplay, no demurrage or delivery terms, no
 * clientContactId, no transportModeId. Sending it at
 * a saved quote would therefore have wiped every one of those, silently, on the
 * first header edit anybody made — which is exactly the bug that the sheet being
 * mounted by nothing had been hiding.
 *
 * So the payload starts from the RECORD and the draft is overlaid on it. Every
 * field the form does not ask about round-trips unchanged, which is the only
 * safe shape for a whole-record PUT reached from a partial form.
 *
 * The alternative — widening QuoteDraft and the create form to ask for all twenty-odd
 * fields — is a bigger form, not a safer save, and it would still leave this
 * function as the thing that guarantees nothing is dropped.
 */
export function toQuoteUpdatePayload(
  record: QuoteDetail,
  draft: QuoteDraft,
): QuotePayload {
  return {
    // The draft's own fields win: these are what the form asked about.
    ...toQuotePayload(draft),

    /* Carried from the record because the form does not ask. Sent as `undefined`
       when the record has none, which is what the record itself means by absent
       — so a quote with none keeps having none rather than acquiring an ''.

       ONE FIELD, and the shrinking of this list from ten to one is the point.
       When this function was written the form asked for about half the wire's
       fields and the other half had to be rescued from the record; the nine that
       were rescued here are now either ENTERABLE — so rescuing them would mean
       the record beating an edit the user just made — or gone from the quote
       altogether. `transportModeId` stays because
       it genuinely is not a header question — the transport mode is chosen on a
       route option (Story 2.4), and this column travels with the header only so
       conversion has somewhere to read it from. */
    transportModeId: record.transportModeId,
  }
}

/** Amends a draft header. The server refuses once the quote is sent (FR-7). */
export function useUpdateQuote() {
  const invalidate = useInvalidateQuotes()
  return useMutation<QuoteSummary, Error, { id: string; payload: QuotePayload }>({
    // A whole-record PUT rather than a patch: a partial body would make
    // "clear the delivery terms" indistinguishable from "leave them alone".
    mutationFn: ({ id, payload }) => api.put<QuoteSummary>(`/quotes/${id}`, payload),
    onSuccess: invalidate,
  })
}

/* Cargo positions (Story 2.2).
 *
 * Not optimistic, and for a sharper reason than the header's. The CHECKLIST is
 * recomputed server-side from the saved positions (FR-12, FR-13), and it is
 * what decides whether the offer may go out. Rendering a position before the
 * server has judged it would show a checklist the server does not agree with —
 * briefly claiming an offer is ready to send when it is not, which is the one
 * thing this screen exists to prevent.
 *
 * So every cargo write invalidates the QUOTE, not just a cargo list: the
 * positions, the checklist and `sendable` arrive together from one endpoint and
 * must never be refreshed apart.
 */

/** The cargo sheet's draft as the server takes it.
 *
 * The measures stay STRINGS all the way through (AD-9) — trimmed, with a comma
 * decimal separator normalised to a dot because that is what a Russian keyboard
 * produces, and the server accepts either. Never Number(): parsing to a float
 * here is exactly how 22.4 becomes 22.399999999999999 on the offer.
 *
 * An empty measure is omitted rather than sent as "": absent is the
 * unmeasured state the checklist reports, and the two must not diverge.
 */
export function toCargoPayload(draft: CargoDraft): CargoPayload {
  const measure = (raw: string): string | undefined => {
    const trimmed = raw.trim().replace(',', '.')
    return trimmed === '' ? undefined : trimmed
  }

  return {
    description: draft.description.trim(),
    hsCode: draft.hsCode.trim() || undefined,
    shipperName: draft.shipperName.trim() || undefined,
    packages: Number(draft.packages),
    packageType: draft.packageType.trim() || undefined,
    weightKg: measure(draft.weightKg),
    volumeCbm: measure(draft.volumeCbm),
    chargeableWeightKg: measure(draft.chargeableWeightKg),
    lengthCm: measure(draft.lengthCm),
    widthCm: measure(draft.widthCm),
    heightCm: measure(draft.heightCm),
    isChemical: draft.isChemical,
    // Only meaningful on chemical cargo (FR-13); the server clears them
    // otherwise, and sending them regardless would be a second opinion.
    hasMsds: draft.isChemical ? draft.hasMsds : undefined,
    hasTransportCertificate: draft.isChemical ? draft.hasTransportCertificate : undefined,
  }
}

function useInvalidateQuote(quoteId: string) {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.quotes.detail(quoteId),
    })
    // The list carries no cargo, but a quote's readiness is what a pipeline
    // reader acts on, so it is refreshed too rather than left stale.
    void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
  }
}

export function useCreateCargo(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CargoPosition, Error, CargoPayload>({
    mutationFn: (payload) => api.post<CargoPosition>(`/quotes/${quoteId}/cargo`, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateCargo(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CargoPosition, Error, { cargoId: string; payload: CargoPayload }>({
    mutationFn: ({ cargoId, payload }) =>
      api.put<CargoPosition>(`/quotes/${quoteId}/cargo/${cargoId}`, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteCargo(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<void, Error, string>({
    mutationFn: (cargoId) => api.delete<void>(`/quotes/${quoteId}/cargo/${cargoId}`),
    onSuccess: invalidate,
  })
}

/* Cost lines (Story 2.3, FR-1).
 *
 * Not optimistic, and the reason is the TOTALS. The margin is computed
 * server-side from the saved lines, and it is the figure a salesperson decides
 * a price on. Rendering a line before the server has judged it would show a
 * margin the server does not agree with — briefly quoting a number that is not
 * the number — which is worse on this surface than a moment of latency.
 *
 * So every cost-line write invalidates the QUOTE: the lines and the totals
 * arrive together from one endpoint and must never be refreshed apart.
 *
 * The comment above about optimistic entry being "the surface this epic owns"
 * described the plan before the totals moved server-side. They are the server's
 * now (FR-1), so this waits — deliberately, and for a better reason than the
 * header does.
 */

/** A cost-line draft as the server takes it — the sheet's and the inline row's
 *  alike, which is why the two declare the same shape.
 *
 * The money stays STRINGS (AD-9) — trimmed, with a comma decimal separator
 * normalised to a dot because that is what a Russian keyboard produces, and the
 * server accepts either. Never Number(): parsing to a float here is exactly how
 * 1234.55 becomes 1234.5499999999999 on the offer.
 *
 * An empty vendor is omitted rather than sent as "": the field is optional, and
 * absent is what the server stores for a line whose agent is not settled yet.
 *
 * The vendor's directory LINK (0044) is the one field here that is always SENT,
 * `null` and all — the exact opposite of the name above it, and deliberately.
 * The server reads an absent key as "this client does not know the field" and
 * leaves the stored link alone, which is right for an older client and wrong
 * for this one: clearing a picked counterparty and typing a name of your own
 * has to reach the database as a cleared link, and an omitted key would leave
 * the old row silently attached to the new name.
 *
 * The ONE price the form collects goes up on BOTH sides, and that is the whole
 * shape of this function. The figure typed is the unit COST — what the source
 * workbook has an operator enter per charge line, from which the customer's
 * price is carried up by the sheet's single profit-margin cell. This
 * application has no column for that cell, so the sell it sends is the cost
 * itself: a markup of ZERO, which is precisely where the workbook sits before
 * anybody types 0.05 into it.
 *
 * What the reader should expect, said plainly because it looks like a defect:
 * a freshly entered line reads **0% margin**, and so does the quote until the
 * markup panel is used. That is the honest state of a costing nobody has priced
 * yet.
 *
 * NOT `sell: "0"` — the mirror of what this used to send, back when the one
 * price was the sell and the cost was the side nobody typed. The OFFER prints
 * the sell. A costing that prints zeros to the client until somebody remembers
 * to press Apply is a trap with no undo behind it, where a costing that prints
 * at cost is merely a deal with no profit in it yet — visible as exactly that on
 * every screen showing a margin. Both figures are sent rather than omitted
 * because the server requires each field present and reports an empty string as
 * `required`.
 *
 * The two sides carry the SAME normalised string, never two separately parsed
 * copies of it: one normalisation, one figure, and no way for the cost and the
 * price of an untouched line to disagree in the last decimal place.
 */
export function toCostLinePayload(draft: CostLineDraft): CostLinePayload {
  const price = (raw: string): string => raw.trim().replace(',', '.')
  const unitPrice = price(draft.unitPrice)

  return {
    description: draft.description.trim(),
    vendorName: draft.vendorName.trim() || undefined,
    // Never `undefined`: see the note above on why this key always travels.
    // A name cleared to nothing takes its link with it — a link with no name is
    // a vendor the offer cannot print, and the server refuses the pair anyway.
    vendorId: draft.vendorName.trim() ? draft.vendorId : null,
    buy: unitPrice,
    sell: unitPrice,
    // A count, not money: the one figure on this form that is genuinely an
    // integer, and the server refuses a zero or a negative.
    quantity: Number(draft.quantity),
  }
}

export function useCreateCostLine(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CostLine, Error, CostLinePayload>({
    mutationFn: (payload) =>
      api.post<CostLine>(`/quotes/${quoteId}/cost-lines`, payload),
    onSuccess: invalidate,
  })
}

/** Inserts a whole preset of charge lines in one request (FR-1).
 *
 *  The labels come from the SETTINGS templates, which the caller reads and hands
 *  over as words — internal/quote reads nothing from internal/settings (AD-1,
 *  AD-2), so the text crosses the boundary through this surface exactly as the
 *  not-included block picker's does.
 *
 *  One request rather than a loop, because a half-failed preset leaves a costing
 *  nobody asked for and seven writes are seven entries in the quote's history. */
export function useAddCostLines(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CostLine[], Error, CostLinePayload[]>({
    mutationFn: async (lines) =>
      (
        await api.post<{ items: CostLine[] }>(`/quotes/${quoteId}/cost-lines/batch`, {
          lines,
        })
      ).items,
    onSuccess: invalidate,
  })
}

/** Prices the whole costing from its cost side (FR-1).
 *
 *  MARKUP over cost, which is the workbook's figure — not the margin over sell
 *  the header shows. The percentage travels as a STRING (AD-9): a JSON number
 *  would arrive as a float and price a costing at 33.329999999999998%.
 *
 *  The server owns the arithmetic and the response is the whole costing, so
 *  nothing here recomputes a total the footer would then disagree with. */
export function useApplyMarkup(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CostLine[], Error, string>({
    mutationFn: async (markupPercent) =>
      (
        await api.post<{ items: CostLine[] }>(`/quotes/${quoteId}/cost-lines/markup`, {
          markupPercent: markupPercent.trim().replace(',', '.'),
        })
      ).items,
    onSuccess: invalidate,
  })
}

export function useUpdateCostLine(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<CostLine, Error, { lineId: string; payload: CostLinePayload }>({
    mutationFn: ({ lineId, payload }) =>
      api.put<CostLine>(`/quotes/${quoteId}/cost-lines/${lineId}`, payload),
    onSuccess: invalidate,
  })
}

export function useDeleteCostLine(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<void, Error, string>({
    mutationFn: (lineId) => api.delete<void>(`/quotes/${quoteId}/cost-lines/${lineId}`),
    onSuccess: invalidate,
  })
}

/* Sending, and opening a new revision (Story 2.6, FR-71).
 *
 * Neither is optimistic, and the reason is the strongest on this screen. A send
 * issues an immutable revision, renders a PDF and enqueues an email to a
 * CLIENT: it is the least reversible action in the product. Rendering it as
 * done before the server has said so would show "Отправлено" over a send that
 * was refused — and the user would not send it again.
 *
 * Both write the returned detail straight into the cache rather than only
 * invalidating. The response already carries the new status, the new revision
 * and both buttons' enablement, so a refetch would show a stale header for a
 * round trip after the most consequential press on the page.
 */

/** The languages an offer can be written in.
 *
 *  The server's own pair, and it is a CLOSED one: `quote.offerLabels` and
 *  `mail.Language` each hold exactly Russian and English, because those are the
 *  two languages this company writes to clients in. Typed rather than left as
 *  `string` so that the preview, the send sheet and the query key cannot drift
 *  onto a third value the server would silently answer in Russian. */
export type OfferLanguage = 'ru' | 'en'

/** Who the offer goes to. Empty means the client's primary contact, which is
 *  what the server defaults to and what the button sends. */
export interface SendQuotePayload {
  recipients?: string[]
  language?: OfferLanguage
}

export interface SendQuoteResult {
  quote: QuoteDetail
  revision: QuoteRevision
  /** Who it actually went to, so the toast can name them rather than say
   *  "sent" and leave the user wondering where. */
  sentTo: string[]
}

/** The offer as it would print, before anything is sent (UX-DR-15).
 *
 * `enabled` is the whole design: the preview is fetched when the SHEET opens,
 * not when the page loads. Composing an offer reads the cargo, the costing and
 * the routings on the server, and doing that on every visit to step 5 would be a
 * document nobody asked to see.
 *
 * `staleTime: 0` for the opposite reason. The user's loop is preview → fix a
 * price → preview again, and a cached document would show them the number they
 * just corrected. Its query key sits under the quote's detail, so an edit
 * invalidates it along with the record.
 *
 * `language` is now REQUIRED, and that is the fix for a real defect rather than
 * a tightening for its own sake. It was optional, every caller omitted it, the
 * server fell back to `defaultOfferLanguage` — Russian — and the preview was
 * therefore Russian for every quote in the system, including the ones about to
 * be sent to a client in English. Making the caller name it means a surface
 * cannot forget to; the query key already varied by it, so two languages cache
 * side by side and switching between them is instant after the first fetch.
 */
export function useOfferPreview(
  quoteId: string,
  { enabled, language }: { enabled: boolean; language: OfferLanguage },
) {
  return useQuery<OfferPreview>({
    queryKey: queryKeys.quotes.offerPreview(quoteId, language),
    queryFn: () =>
      api.get<OfferPreview>(
        `/quotes/${quoteId}/offer?language=${encodeURIComponent(language)}`,
      ),
    enabled: enabled && quoteId !== '',
    staleTime: 0,
  })
}

export function useSendQuote(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation<SendQuoteResult, Error, SendQuotePayload | void>({
    mutationFn: (payload) =>
      api.post<SendQuoteResult>(`/quotes/${quoteId}/send`, payload ?? {}),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.quotes.detail(quoteId), withMode(result.quote))
      // The pipeline shows status, and a quote that just moved to Sent must not
      // sit in the Draft column behind the user. The win/loss figures count the
      // same movement — a send adds to `open` and to the `total` the rate is
      // read against — so they are refreshed with it rather than left to the
      // next focus.
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.statisticsAll })
    },
  })
}

/** Opens a new revision, which reopens the editing window (FR-6, FR-7).
 *
 * The already-issued revisions are untouched — that is what makes this safe to
 * offer from a Sent quote. What reopens is the live header. */
export function useReopenQuote(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation<QuoteDetail, Error, void>({
    mutationFn: () => api.post<QuoteDetail>(`/quotes/${quoteId}/revisions`, {}),
    onSuccess: (quote) => {
      queryClient.setQueryData(queryKeys.quotes.detail(quoteId), withMode(quote))
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.list() })
    },
  })
}

/* The parties on the shipment, once the quote exists (FR-24).
 *
 * The create form submits its parties INSIDE the create payload, because
 * there is no quote id yet to hang them on — see toQuotePayload. These three
 * are the other half: a party added, corrected or removed on a quote that has
 * already been saved.
 *
 * None of them is optimistic, and the reason is the same one useAddParticipant
 * gives on the case side: the response may come back SUPPRESSED. An optimistic
 * row would render the name the user typed and then replace it with "agent
 * hidden" when the server answered — a leak with a timer on it (FR-29, AD-6).
 * The server also decides the party's position in the list, and a client
 * guessing it would reorder the card grid under the reader's cursor.
 *
 * The add is also the only one of the three that notifies anybody: adding a
 * party to a quote somebody ELSE owns tells the owner. The client does nothing
 * to make that happen and must not try — it is the server's decision, made in
 * the same transaction as the write.
 */

/** A party as the server takes it.
 *
 *  Beside the mutations rather than beside the sheet, for the reason
 *  toQuotePayload and toCargoPayload are: two call sites submitting the same
 *  record must not disagree about what a blank contact means.
 *
 *  The contact is dropped when blank EVEN IF `notifyByEmail` is set, which
 *  looks like a bug and is not: that combination is exactly what the server
 *  refuses, and sending it is what makes the refusal land on the contact field
 *  where the user can fix it. Suppressing it here would send a valid-looking
 *  payload that fails a constraint instead. */
export function toQuoteParticipantPayload(draft: {
  role: ParticipantPayload['role']
  name: string
  contact: string
  notifyByEmail: boolean
}): ParticipantPayload {
  return {
    role: draft.role,
    name: draft.name.trim(),
    contact: draft.contact.trim() || undefined,
    notifyByEmail: draft.notifyByEmail,
  }
}

export function useAddQuoteParticipant(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<QuoteParticipant, Error, ParticipantPayload>({
    mutationFn: (payload) =>
      api.post<QuoteParticipant>(`/quotes/${quoteId}/participants`, payload),
    onSuccess: invalidate,
  })
}

export function useUpdateQuoteParticipant(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<
    QuoteParticipant,
    Error,
    { participantId: string; payload: ParticipantPayload }
  >({
    mutationFn: ({ participantId, payload }) =>
      api.put<QuoteParticipant>(
        `/quotes/${quoteId}/participants/${participantId}`,
        payload,
      ),
    onSuccess: invalidate,
  })
}

/** Removes a party. It does not unsend anything — a notification already in
 *  somebody's panel stays there, naming a party who is no longer on the quote.
 *  That is the honest record of what happened and is not worth reversing. */
export function useRemoveQuoteParticipant(quoteId: string) {
  const invalidate = useInvalidateQuote(quoteId)
  return useMutation<void, Error, string>({
    mutationFn: (participantId) =>
      api.delete<void>(`/quotes/${quoteId}/participants/${participantId}`),
    onSuccess: invalidate,
  })
}

/* ── the internal team (migration 0028) ──────────────────────────────────────
 *
 * The colleagues on a quote, as opposed to the participants above — those are
 * EXTERNAL parties with no login, these are staff accounts. The server keeps
 * them in two tables and the client keeps them in two hooks for the same
 * reason: one is a shipper, the other is who at ASA is working the deal.
 *
 * Membership is also what the quote's row scope reads, which is why none of
 * these is optimistic. A colleague shown as assigned before the server agreed is
 * a screen claiming somebody can see a quote they cannot — and the refusals
 * these can meet (a deactivated account, the last member) are ones the user must
 * be told about rather than having a row appear and then vanish.
 */

export function useQuoteTeam(quoteId: string) {
  return useQuery<QuoteTeamMember[]>({
    queryKey: queryKeys.quotes.team(quoteId),
    queryFn: async () =>
      (await api.get<{ items: QuoteTeamMember[] }>(`/quotes/${quoteId}/team`)).items,
  })
}

/** Staffs a quote (FR-14, FR-57), and — with a reason — asks for a price.
 *
 *  The server answers with the WHOLE team, so nothing here splices a list — a
 *  spliced list is one that can disagree with the server about who is on the
 *  quote, which for a relation that grants visibility is the disagreement that
 *  matters most.
 *
 *  `reason` decides which event the assignee receives: a plain staffing tells
 *  them they may look, a pricing request tells them somebody is waiting for
 *  their number. Two topics rather than one, because the two acts are the same
 *  shape and different meanings (quote.TopicQuotePricingRequested). `note` is
 *  the sentence the asker typed and travels with it.
 *
 *  Both are optional here so a card that only staffs stays a one-key call. */
export function useAssignQuoteTeamMember(quoteId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    QuoteTeamMember[],
    Error,
    { userId: string; reason?: 'pricing'; note?: string }
  >({
    mutationFn: async ({ userId, reason, note }) =>
      (
        await api.post<{ items: QuoteTeamMember[] }>(`/quotes/${quoteId}/team`, {
          userId,
          // Stated, never inferred from the assignee's role: roles are dynamic
          // and this client is forbidden from switching on them.
          reason: reason ?? '',
          note: note ?? '',
        })
      ).items,
    onSuccess: (items) => {
      queryClient.setQueryData(queryKeys.quotes.team(quoteId), items)
      // The detail read carries the team, so it must not keep serving a stale
      // copy beside the fresh one above.
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.detail(quoteId) })
    },
  })
}

/** Unstaffs a quote (FR-14, FR-56).
 *
 *  Access ends at the removed colleague's next request: the team IS the scope
 *  predicate, so there is no cached grant to expire. */
export function useRemoveQuoteTeamMember(quoteId: string) {
  const queryClient = useQueryClient()

  return useMutation<QuoteTeamMember[], Error, string>({
    mutationFn: async (userId) =>
      (
        await api.delete<{ items: QuoteTeamMember[] }>(
          `/quotes/${quoteId}/team/${userId}`,
        )
      ).items,
    onSuccess: (items) => {
      queryClient.setQueryData(queryKeys.quotes.team(quoteId), items)
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotes.detail(quoteId) })
    },
  })
}
